// frontend/src/pages/Store.tsx
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { ButtonGroup, SimpleGrid } from '@chakra-ui/react'
import {
  Box,
  Button,
  Text,
  Stack,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  HStack,
  IconButton,
  Image,
  Tooltip
} from '@chakra-ui/react'
import QRCode from 'react-qr-code' // ✅ 추가
import { Input } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useMemo, useState } from 'react'
import { CopyIcon } from '@chakra-ui/icons'
import BigNumber from 'bignumber.js'
import { buyPass, depositBSC, depositTRC } from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import CheckAuth from '../components/layouts/CheckAuth'

type PlanType = 'BASIC' | 'VIP' | 'SECRET' | 'SECRET+'
type ChainType = 'BSC' | 'TRC20'

// 🔸 TRC 고정 주소
const TRC_FIXED_ADDRESS = 'TK1orQAKS1iFSsF3aropohpUZZke1E6Xcf'

const Store = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const [auth] = useRecoilState(authState)
  const { reloadUser } = useAuthState()
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [isDepositing, setIsDepositing] = useState(false)

  // 🔹 추가: 네트워크 선택 (기본 BSC)
  const [selectedChain, setSelectedChain] = useState<ChainType>('BSC')

  // 🔹 현재 선택한 네트워크의 주소/라벨
  const currentAddress = useMemo(() => {
    return selectedChain === 'BSC'
      ? auth?.bscDepositWalletAddress || ''
      : TRC_FIXED_ADDRESS
  }, [selectedChain, auth])

  // 🔸 TRC txid 입력 상태
  const [trcTxid, setTrcTxid] = useState('')
  const trcTxidValid = useMemo(() => {
    const v = trcTxid.trim()
    return /^[A-Fa-f0-9]{64}$/.test(v)
  }, [trcTxid])

  const currentNetworkLabel =
    selectedChain === 'BSC' ? t('BSC (BNB Chain)') : t('TRC20 (TRON)')

  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose
  } = useDisclosure()

  const {
    isOpen: isDepositOpen,
    onOpen: onDepositOpen,
    onClose: onDepositClose
  } = useDisclosure()

  const plans = [
    {
      name: 'BASIC',
      duration: '1달 (30일)',
      price: 300,
      description: '자동매매 서비스를 가장 싼 가격에 이용할 수 있습니다',
      tag: '추천'
    },
    {
      name: 'VIP',
      duration: '1년 (365일) + 2달(60일)',
      price: 3600,
      description: '2달 추가 제공 + 선착순 1000명 레퍼럴 수익 10% 분배',
      tag: '인기'
    },
    {
      name: 'SECRET',
      duration: '2년 (730일)',
      price: 5000,
      description:
        '긴 구독 기간에 따른 가격 할인 + 선착순 1000명 구독료 수익 10% 분배 + 선착순 1000명 레퍼럴 수익 10% 분배',
      tag: '혜택'
    },
    {
      name: 'SECRET+',
      duration: '3년 (1095일)',
      price: 7000,
      description:
        '가장 낮은 월 단가로, 최대 3년 구독 혜택 제공. 인플레이션 걱정 없는 고정가 혜택 + 선착순 1000명 구독료 수익 10% 분배 + 선착순 1000명 레퍼럴 수익 10% 분배',
      tag: '혜택'
    }
  ]

  const handlePurchase = async () => {
    if (!selectedPlan || isPurchasing) return
    setIsPurchasing(true)

    try {
      const { data: res } = await buyPass({
        product: selectedPlan
      })
      if (res.statusCode === 200) {
        toast({
          title: t('{{plan}} 플랜이 구매되었습니다.', { plan: selectedPlan }),
          status: 'success',
          duration: 2000,
          isClosable: true
        })

        onConfirmClose()
        reloadUser()
      }
    } catch (err: any) {
      toast({
        title: t('이용권구매'),
        description:
          err.response?.data?.message || t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleDeposit = async () => {
    if (isDepositing) return
    setIsDepositing(true)
    try {
      if (selectedChain === 'BSC') {
        const { data: res } = await depositBSC()
        if (res.statusCode === 200) {
          toast({
            title: t('입금 요청 처리됨'),
            description: t('{{network}} 입금 확인을 진행합니다.', {
              network: currentNetworkLabel
            }),
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          onDepositClose()
          reloadUser()
        }
      } else {
        if (!trcTxidValid) throw new Error(t('유효한 TRON TXID를 입력하세요.'))
        const { data: res } = await depositTRC({ txId: trcTxid.trim() })
        if (res.statusCode === 200) {
          toast({
            title: t('입금 요청 접수'),
            description: t('TXID를 확인하고 입금 반영을 진행합니다.'),
            status: 'success',
            duration: 2000,
            isClosable: true
          })
          onDepositClose()
          setTrcTxid('')
          reloadUser()
        }
      }
    } catch (err: any) {
      toast({
        title: t('{{network}} 입금', { network: currentNetworkLabel }),
        description:
          err?.response?.data?.message ||
          err?.message ||
          t('알 수 없는 오류가 발생했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsDepositing(false)
    }
  }

  const handleCopy = async () => {
    try {
      if (!currentAddress) throw new Error('no address')
      await navigator.clipboard.writeText(currentAddress)
      toast({
        title: t('지갑 주소가 복사되었습니다.'),
        description: currentNetworkLabel,
        status: 'success',
        duration: 1500,
        isClosable: true
      })
    } catch {
      toast({
        title: t('복사 실패'),
        status: 'error',
        duration: 1500,
        isClosable: true
      })
    }
  }

  return (
    <DefaultLayout>
      <Box maxW="4xl" mx="auto" p={6}>
        <Text fontSize="2xl" fontWeight="bold" mb={4} color="white">
          {t('구독 상품')}
        </Text>

        {auth.id && (
          <HStack justify="space-between" mb={6} bg="dark.card" p={4} borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100">
            <HStack spacing={2}>
              <Text fontSize="md" color="gray.400">
                💰 <b>{t('보유 USDT')}</b>
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="cyan.400">
                {new BigNumber(auth.usdtBalance).toFormat(0)} USDT
              </Text>
            </HStack>

            <Button
              size="sm"
              onClick={onDepositOpen}
              colorScheme="cyan"
              variant="outline"
              _hover={{ bg: 'whiteAlpha.100' }}
            >
              {t('충전하기')}
            </Button>
          </HStack>
        )}

        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 2 }}
          spacing={6}
          mt={6}
          justifyItems="center"
        >
          {plans.map((plan, idx) => (
            <Box
              key={plan.name}
              p={0}
              border="1px solid"
              borderColor="whiteAlpha.100"
              borderRadius="xl"
              overflow="hidden"
              boxShadow="lg"
              bg="dark.card"
              transition="all 0.3s ease"
              _hover={{
                transform: 'scale(1.03)',
                boxShadow: '0 0 20px rgba(0, 200, 255, 0.3)',
                borderColor: 'brand.500'
              }}
              maxW="sm"
              w="100%"
            >
              <Box
                bgImage={`/images/p${idx + 1}.jpg`}
                bgSize="cover"
                bgPosition="center"
                h="160px"
                position="relative"
              >
                <Box position="absolute" top={0} left={0} w="full" h="full" bg="blackAlpha.400" />
              </Box>

              <Box p={6}>
                <HStack justifyContent="space-between" mb={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="white">
                    {plan.name} {t('플랜')}
                  </Text>
                  {plan.tag && (
                    <Text
                      fontSize="sm"
                      color="white"
                      bg={
                        plan.tag === '인기'
                          ? 'blue.500'
                          : plan.tag === '혜택'
                          ? 'green.500'
                          : 'cyan.500'
                      }
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontWeight="bold"
                      boxShadow="0 0 10px rgba(0,0,0,0.3)"
                    >
                      {t(plan.tag)}
                    </Text>
                  )}
                </HStack>

                <Text fontSize="sm" color="gray.400" mb={1}>
                  {t(plan.duration)} {t('구독')}
                </Text>

                <Text fontSize="md" color="gray.300" mb={4} minH="48px">
                  {t(plan.description)}
                </Text>

                <Text fontSize="2xl" fontWeight="bold" color="cyan.400" mb={6}>
                  {plan.price.toLocaleString()} USDT
                </Text>

                {auth.id && (
                  <Button
                    w="100%"
                    colorScheme="cyan"
                    size="lg"
                    onClick={() => {
                      setSelectedPlan(plan.name as PlanType)
                      onConfirmOpen()
                    }}
                    bgGradient="linear(to-r, cyan.500, blue.500)"
                    _hover={{
                        bgGradient: "linear(to-r, cyan.400, blue.400)",
                        boxShadow: "0 0 15px rgba(0, 200, 255, 0.5)"
                    }}
                  >
                    {t('구매하기')}
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* 구매 확인 모달 */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
          <ModalHeader color="white">{t('구매 확인')}</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Text color="gray.300">
              {t('{{plan}} 플랜을 정말로 구매하시겠습니까?', {
                plan: selectedPlan || ''
              })}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose} color="gray.400" _hover={{ bg: 'whiteAlpha.100', color: 'white' }}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="cyan"
              onClick={handlePurchase}
              isLoading={isPurchasing}
              loadingText={t('구매 중...')}
            >
              {t('구매 확정')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 충전 모달 */}
      <Modal isOpen={isDepositOpen} onClose={onDepositClose} isCentered>
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg="dark.card" border="1px solid" borderColor="whiteAlpha.200">
          <ModalHeader color="white">{t('USDT 충전')}</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Stack spacing={4} align="center" textAlign="center">
              {/* 네트워크 선택 */}
              <ButtonGroup isAttached variant="outline" size="sm">
                <Button
                  onClick={() => setSelectedChain('BSC')}
                  colorScheme={selectedChain === 'BSC' ? 'yellow' : 'gray'}
                  variant={selectedChain === 'BSC' ? 'solid' : 'outline'}
                  _hover={{ bg: selectedChain === 'BSC' ? 'yellow.500' : 'whiteAlpha.100' }}
                  color={selectedChain === 'BSC' ? 'black' : 'gray.400'}
                  borderColor="whiteAlpha.300"
                >
                  {t('BSC (BNB Chain)')}
                </Button>
                <Button
                  onClick={() => setSelectedChain('TRC20')}
                  colorScheme={selectedChain === 'TRC20' ? 'red' : 'gray'}
                  variant={selectedChain === 'TRC20' ? 'solid' : 'outline'}
                  _hover={{ bg: selectedChain === 'TRC20' ? 'red.500' : 'whiteAlpha.100' }}
                  color={selectedChain === 'TRC20' ? 'white' : 'gray.400'}
                  borderColor="whiteAlpha.300"
                >
                  {t('TRC20 (TRON)')}
                </Button>
              </ButtonGroup>

              <Text fontSize="sm" color="gray.400">
                {t('아래 지갑 주소로')} <strong style={{ color: '#4FD1C5' }}>USDT</strong>{' '}
                {t('를 입금해주세요.')}
              </Text>

              {/* 공통: 주소/QR */}
              <Box bg="white" p={4} borderRadius="xl">
                <QRCode value={currentAddress || ''} size={110} />
              </Box>

              <HStack bg="whiteAlpha.100" p={2} borderRadius="md" w="full" justify="center">
                <Text fontSize="sm" fontWeight="bold" isTruncated maxW="240px" color="white">
                  {currentAddress || t('(주소 없음)')}
                </Text>
                <Tooltip label={t('복사')}>
                  <IconButton
                    icon={<CopyIcon />}
                    size="sm"
                    aria-label={t('복사')}
                    onClick={handleCopy}
                    isDisabled={!currentAddress}
                    variant="ghost"
                    color="cyan.400"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />
                </Tooltip>
              </HStack>

              {/* 네트워크 주의 박스 */}
              <Box
                bg={selectedChain === 'BSC' ? 'yellow.900' : 'red.900'}
                p={3}
                borderRadius="md"
                w="100%"
                border="1px solid"
                borderColor={selectedChain === 'BSC' ? 'yellow.500' : 'red.500'}
              >
                <Text
                  fontSize="sm"
                  color={selectedChain === 'BSC' ? 'yellow.200' : 'red.200'}
                >
                  {t(
                    '⚠️ 반드시 {{network}} 네트워크로 전송해주세요. 다른 네트워크로 전송 시 복구가 불가능합니다.',
                    { network: currentNetworkLabel }
                  )}
                </Text>
              </Box>

              {/* 네트워크별 액션 */}
              {selectedChain === 'BSC' ? (
                <>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {t('입금 후 아래 "입금 완료" 버튼을 클릭해야 충전됩니다.')}
                  </Text>
                  <Button
                    w="100%"
                    colorScheme="cyan"
                    bgGradient="linear(to-r, cyan.500, blue.500)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, cyan.400, blue.400)",
                      boxShadow: "0 0 15px rgba(0, 200, 255, 0.5)"
                    }}
                    onClick={handleDeposit}
                    isDisabled={!currentAddress}
                    isLoading={isDepositing}
                    loadingText={t('처리 중...')}
                  >
                    {t('{{network}} 입금 완료', {
                      network: currentNetworkLabel
                    })}
                  </Button>
                </>
              ) : (
                <>
                  <Stack w="100%" spacing={2} mt={2}>
                    <Text fontSize="sm" color="gray.400" textAlign="left">
                      USDT {t('전송 후')} <b>TXID</b> {t('를 입력하세요.')}
                    </Text>
                    <Input
                      placeholder={t('예: a3f9... (64자리 hex)')}
                      value={trcTxid}
                      onChange={(e) => setTrcTxid(e.target.value.trim())}
                      isInvalid={trcTxid.length > 0 && !trcTxidValid}
                      bg="dark.bg"
                      borderColor="whiteAlpha.300"
                      color="white"
                      _placeholder={{ color: 'gray.600' }}
                    />
                    {trcTxid && !trcTxidValid && (
                      <Text fontSize="xs" color="red.400">
                        {t('유효한 TRON TXID 형식(64자리 hex)이 아닙니다.')}
                      </Text>
                    )}

                    <Text fontSize="xs" color="cyan.400">
                      {t(
                        '⚠️ 관리자가 TXID를 확인 후 처리하므로 완료까지 시간이 다소 소요될 수 있습니다.'
                      )}
                    </Text>
                  </Stack>

                  <Button
                    w="100%"
                    colorScheme="cyan"
                    bgGradient="linear(to-r, cyan.500, blue.500)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, cyan.400, blue.400)",
                      boxShadow: "0 0 15px rgba(0, 200, 255, 0.5)"
                    }}
                    onClick={handleDeposit}
                    isDisabled={!currentAddress || !trcTxidValid}
                    isLoading={isDepositing}
                    loadingText={t('처리 중...')}
                  >
                    {t('TRC20 입금 확인 요청')}
                  </Button>

                  {trcTxidValid && (
                    <Tooltip label={t('Tronscan에서 TX 조회')}>
                      <Button
                        as="a"
                        href={`https://tronscan.org/#/transaction/${trcTxid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="ghost"
                        color="gray.400"
                        _hover={{ color: 'white' }}
                      >
                        {t('Tronscan 열기')}
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
            </Stack>
          </ModalBody>

          <ModalFooter />
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default Store
